/**
 * Popup — дирижёр поведения шапочного попапа.
 *
 * Класс не владеет DOM'ом попапа — его рисует Vue-компонент. Класс получает
 * готовые DOM-элементы триггера и попапа и отвечает за:
 *   1. Позиционирование попапа относительно триггера (top/left в стиле).
 *   2. Рисование невидимой SVG-"тропинки" между триггером и попапом,
 *      сохраняющей hover при движении курсора через зазор offset
 *      (иначе :hover моргает — курсор "вываливается в пустоту").
 *   3. Закрытие по клику вне триггера и попапа.
 *   4. Репозиционирование при скролле/ресайзе окна.
 *
 * Vue-слой:
 *   - рисует <Teleport> с разметкой попапа и слотами
 *   - передаёт сюда два HTMLElement и колбэк onClose
 *   - в lifecycle вызывает attach()/detach()
 */

export type PopupSide  = 'top' | 'right' | 'bottom' | 'left';
export type PopupAlign = 'start' | 'center' | 'end';

type Rect = { top: number; left: number; width: number; height: number };

const FORGIVING_PATH_OVERLAP_PX = 1; // антиалиасинг: микро-нахлёст SVG на попап, чтобы не было "светлой щели"

class Popup {
	trigger: HTMLElement;
	popup:   HTMLElement;
	side:    PopupSide;
	align:   PopupAlign;
	offset:  number;
	onClose: () => void;

	private svg: SVGSVGElement | null = null;

	constructor(
		trigger: HTMLElement,
		popup:   HTMLElement,
		side:    PopupSide,
		align:   PopupAlign,
		offset:  number,
		onClose: () => void,
	) {
		this.trigger = trigger;
		this.popup   = popup;
		this.side    = side;
		this.align   = align;
		this.offset  = offset;
		this.onClose = onClose;
	}

	// ─── Жизненный цикл ─────────────────────────────────────────────────────

	attach(): void {
		this.createForgivingSvg();
		this.reposition();

		window.addEventListener('scroll',      this.onReposition, { capture: true, passive: true });
		window.addEventListener('resize',      this.onReposition);
		window.addEventListener('mousedown',   this.onOutsideMousedown);
	}

	detach(): void {
		window.removeEventListener('scroll',    this.onReposition, { capture: true } as EventListenerOptions);
		window.removeEventListener('resize',    this.onReposition);
		window.removeEventListener('mousedown', this.onOutsideMousedown);

		if (this.svg && this.svg.parentNode) {
			this.svg.parentNode.removeChild(this.svg);
		}
		this.svg = null;
	}

	reposition(): void {
		const triggerRect: Rect = Popup.getRect(this.trigger);
		const popupSize:   { width: number; height: number } = {
			width:  this.popup.offsetWidth,
			height: this.popup.offsetHeight,
		};

		const popupCoords: { top: number; left: number }
			= this.calculatePopupCoords(triggerRect, popupSize);

		this.popup.style.top  = `${popupCoords.top}px`;
		this.popup.style.left = `${popupCoords.left}px`;

		this.updateForgivingSvg(triggerRect, popupSize, popupCoords);
	}

	// ─── Листенеры как arrow-поля — чтобы this не терялся и removeEventListener ловил ту же ссылку ───

	private onReposition = (): void => {
		this.reposition();
	};

	private onOutsideMousedown = (event: MouseEvent): void => {
		const target:        Node = event.target as Node;
		const clickedTrigger:  boolean = this.trigger.contains(target);
		const clickedPopup:    boolean = this.popup.contains(target);

		if (clickedTrigger === false && clickedPopup === false) {
			this.onClose();
		}
	};

	// ─── Позиционирование попапа ────────────────────────────────────────────

	/**
	 * Считает координаты попапа в viewport для выбранной стороны и выравнивания.
	 * Попап прижат через top/left (position: fixed), поэтому координаты — от верх-лево viewport.
	 * После расчёта ограничиваем попап в пределах окна с отступом 8px.
	 */
	private calculatePopupCoords(
		triggerRect: Rect,
		popupSize:   { width: number; height: number },
	): { top: number; left: number } {
		let top:  number = 0;
		let left: number = 0;

		switch (this.side) {
			case 'top':
				top = triggerRect.top - this.offset - popupSize.height;
				left = this.alignHorizontal(triggerRect, popupSize.width);
				break;
			case 'bottom':
				top = triggerRect.top + triggerRect.height + this.offset;
				left = this.alignHorizontal(triggerRect, popupSize.width);
				break;
			case 'left':
				left = triggerRect.left - this.offset - popupSize.width;
				top  = this.alignVertical(triggerRect, popupSize.height);
				break;
			case 'right':
				left = triggerRect.left + triggerRect.width + this.offset;
				top  = this.alignVertical(triggerRect, popupSize.height);
				break;
		}

		// Не даём попапу вылезти за края viewport — зажимаем с отступом 8px
		const VIEWPORT_MARGIN: number = 8;
		const maxLeft: number = window.innerWidth  - popupSize.width  - VIEWPORT_MARGIN;
		const maxTop:  number = window.innerHeight - popupSize.height - VIEWPORT_MARGIN;
		left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));
		top  = Math.max(VIEWPORT_MARGIN, Math.min(top,  maxTop));

		return { top, left };
	}

	/**
	 * Выравнивание по горизонтали — актуально для side=top|bottom.
	 *   start  — левый край попапа у левого края триггера
	 *   end    — правый край попапа у правого края триггера
	 *   center — центры совпадают
	 */
	private alignHorizontal(triggerRect: Rect, popupWidth: number): number {
		switch (this.align) {
			case 'start':  return triggerRect.left;
			case 'end':    return triggerRect.left + triggerRect.width - popupWidth;
			case 'center': return triggerRect.left + triggerRect.width / 2 - popupWidth / 2;
		}
	}

	/**
	 * Выравнивание по вертикали — актуально для side=left|right.
	 */
	private alignVertical(triggerRect: Rect, popupHeight: number): number {
		switch (this.align) {
			case 'start':  return triggerRect.top;
			case 'end':    return triggerRect.top + triggerRect.height - popupHeight;
			case 'center': return triggerRect.top + triggerRect.height / 2 - popupHeight / 2;
		}
	}

	// ─── Forgiving SVG path ─────────────────────────────────────────────────

	/**
	 * Создаёт <svg> с <path> внутри попапа. SVG — прозрачный, но ловит курсор
	 * (pointer-events заданы в CSS). Размеры/путь заполнятся в updateForgivingSvg().
	 */
	private createForgivingSvg(): void {
		const svg:  SVGSVGElement  = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
		const path: SVGPathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');

		svg.classList.add('header-popup__forgiving');
		svg.appendChild(path);
		this.popup.appendChild(svg);

		this.svg = svg;
	}

	/**
	 * Пересчитывает геометрию моста между триггером и попапом и пишет её в SVG.
	 */
	private updateForgivingSvg(
		triggerRect: Rect,
		popupSize:   { width: number; height: number },
		popupCoords: { top: number; left: number },
	): void {
		if (this.svg === null) return;

		const shape: { svgStyle: Rect; pathD: string } = Popup.buildForgivingShape(
			this.side,
			this.offset,
			triggerRect,
			popupSize,
			popupCoords,
		);

		this.svg.setAttribute('viewBox', `0 0 ${shape.svgStyle.width} ${shape.svgStyle.height}`);
		this.svg.style.top    = `${shape.svgStyle.top}px`;
		this.svg.style.left   = `${shape.svgStyle.left}px`;
		this.svg.style.width  = `${shape.svgStyle.width}px`;
		this.svg.style.height = `${shape.svgStyle.height}px`;

		const pathEl: SVGPathElement | null = this.svg.querySelector('path');
		if (pathEl !== null) {
			pathEl.setAttribute('d', shape.pathD);
		}
	}

	// ─── Геометрия формы forgiving path (чистая математика, без DOM) ────────

	/**
	 * Возвращает где поставить <svg> (позиция/размер относительно попапа) и
	 * какой d-атрибут у <path>. Чистая функция — вся работа с DOM снаружи.
	 *
	 * Четыре ветки по стороне, в каждой — задача одна и та же: построить
	 * фигуру, которая соединяет сторону попапа с противоположной стороной
	 * триггера через зазор offset, с плавными Q-изгибами в углах.
	 *
	 * Система координат каждой фигуры — локальная (0,0) в левом верхнем углу
	 * SVG. anchorInSvg — координаты левого верхнего угла триггера в этой системе.
	 */
	private static buildForgivingShape(
		side:        PopupSide,
		offset:      number,
		triggerRect: Rect,
		popupSize:   { width: number; height: number },
		popupCoords: { top: number; left: number },
	): { svgStyle: Rect; pathD: string } {
		switch (side) {
			case 'bottom': return Popup.buildForgivingShapeBottom(offset, triggerRect, popupSize, popupCoords);
			case 'top':    return Popup.buildForgivingShapeTop(   offset, triggerRect, popupSize, popupCoords);
			case 'right':  return Popup.buildForgivingShapeRight( offset, triggerRect, popupSize, popupCoords);
			case 'left':   return Popup.buildForgivingShapeLeft(  offset, triggerRect, popupSize, popupCoords);
		}
	}

	/**
	 * Попап снизу якоря. SVG стоит НАД попапом, охватывая якорь + зазор.
	 *
	 *     ┌─ якорь ─┐
	 *     │         │
	 *     └─┐     ┌─┘
	 *       ╲     ╱       ← плавные Q-изгибы
	 *   ────┴─────┴────
	 *   │    попап     │
	 */
	private static buildForgivingShapeBottom(
		offset:      number,
		triggerRect: Rect,
		popupSize:   { width: number; height: number },
		popupCoords: { top: number; left: number },
	): { svgStyle: Rect; pathD: string } {
		const svgStyle: Rect = {
			top:    -(triggerRect.height + offset),
			left:   triggerRect.left < popupCoords.left ? triggerRect.left - popupCoords.left : 0,
			width:  Math.max(popupSize.width, triggerRect.width),
			height: triggerRect.height + offset,
		};

		// Координаты левого верхнего угла триггера в локальной системе SVG
		const anchorLeftInSvg: number = triggerRect.left > popupCoords.left ? triggerRect.left - popupCoords.left : 0;
		const anchorTopInSvg:  number = 0;

		const d: string[] = [];
		d.push(`M ${anchorLeftInSvg} ${anchorTopInSvg}`);
		d.push(`Q ${anchorLeftInSvg} ${svgStyle.height} 0 ${svgStyle.height}`);
		d.push(`h ${svgStyle.width}`);
		d.push(`Q ${anchorLeftInSvg + triggerRect.width} ${svgStyle.height} ${anchorLeftInSvg + triggerRect.width - FORGIVING_PATH_OVERLAP_PX} 0`);
		d.push(`v ${triggerRect.height - FORGIVING_PATH_OVERLAP_PX}`);
		d.push(`h -${triggerRect.width}`);
		d.push(`z`);

		return { svgStyle, pathD: d.join(' ') };
	}

	/**
	 * Попап сверху якоря. SVG стоит ПОД попапом, охватывая зазор + якорь.
	 */
	private static buildForgivingShapeTop(
		offset:      number,
		triggerRect: Rect,
		popupSize:   { width: number; height: number },
		popupCoords: { top: number; left: number },
	): { svgStyle: Rect; pathD: string } {
		const svgStyle: Rect = {
			top:    popupSize.height,
			left:   triggerRect.left < popupCoords.left ? triggerRect.left - popupCoords.left : 0,
			width:  Math.max(popupSize.width, triggerRect.width),
			height: triggerRect.height + offset,
		};

		const anchorLeftInSvg: number = triggerRect.left > popupCoords.left ? triggerRect.left - popupCoords.left : 0;
		const anchorTopInSvg:  number = triggerRect.top - (popupCoords.top + svgStyle.top);

		const d: string[] = [];
		d.push(`M ${anchorLeftInSvg} ${anchorTopInSvg}`);
		d.push(`v ${triggerRect.height}`);
		d.push(`Q ${anchorLeftInSvg} 0 0 0`);
		d.push(`h ${svgStyle.width}`);
		d.push(`Q ${anchorLeftInSvg + triggerRect.width} 0 ${anchorLeftInSvg + triggerRect.width - FORGIVING_PATH_OVERLAP_PX} ${svgStyle.height}`);
		d.push(`v -${triggerRect.height - FORGIVING_PATH_OVERLAP_PX}`);
		d.push(`z`);

		return { svgStyle, pathD: d.join(' ') };
	}

	/**
	 * Попап справа от якоря. SVG стоит СЛЕВА от попапа.
	 */
	private static buildForgivingShapeRight(
		offset:      number,
		triggerRect: Rect,
		popupSize:   { width: number; height: number },
		popupCoords: { top: number; left: number },
	): { svgStyle: Rect; pathD: string } {
		const svgStyle: Rect = {
			top:    triggerRect.top < popupCoords.top ? triggerRect.top - popupCoords.top : 0,
			left:   -(triggerRect.width + offset),
			width:  triggerRect.width + offset,
			height: Math.max(popupSize.height, triggerRect.height),
		};

		const anchorTopInSvg:  number = triggerRect.top > popupCoords.top ? triggerRect.top - popupCoords.top : 0;
		const anchorLeftInSvg: number = 0;

		const d: string[] = [];
		d.push(`M ${anchorLeftInSvg} ${anchorTopInSvg}`);
		d.push(`Q ${svgStyle.width} ${anchorTopInSvg} ${svgStyle.width} 0`);
		d.push(`v ${svgStyle.height}`);
		d.push(`Q ${svgStyle.width} ${anchorTopInSvg + triggerRect.height} ${anchorLeftInSvg} ${anchorTopInSvg + triggerRect.height - FORGIVING_PATH_OVERLAP_PX}`);
		d.push(`h ${triggerRect.width - FORGIVING_PATH_OVERLAP_PX}`);
		d.push(`v -${triggerRect.height}`);
		d.push(`z`);

		return { svgStyle, pathD: d.join(' ') };
	}

	/**
	 * Попап слева от якоря. SVG стоит СПРАВА от попапа.
	 */
	private static buildForgivingShapeLeft(
		offset:      number,
		triggerRect: Rect,
		popupSize:   { width: number; height: number },
		popupCoords: { top: number; left: number },
	): { svgStyle: Rect; pathD: string } {
		const svgStyle: Rect = {
			top:    triggerRect.top < popupCoords.top ? triggerRect.top - popupCoords.top : 0,
			left:   popupSize.width,
			width:  triggerRect.width + offset,
			height: Math.max(popupSize.height, triggerRect.height),
		};

		const anchorTopInSvg:  number = triggerRect.top > popupCoords.top ? triggerRect.top - popupCoords.top : 0;
		const anchorLeftInSvg: number = triggerRect.left - (popupCoords.left + popupSize.width);

		const d: string[] = [];
		d.push(`M ${anchorLeftInSvg} ${anchorTopInSvg}`);
		d.push(`h ${triggerRect.width}`);
		d.push(`Q 0 ${anchorTopInSvg} 0 0`);
		d.push(`v ${svgStyle.height}`);
		d.push(`Q 0 ${anchorTopInSvg + triggerRect.height} ${svgStyle.width - FORGIVING_PATH_OVERLAP_PX} ${anchorTopInSvg + triggerRect.height - FORGIVING_PATH_OVERLAP_PX}`);
		d.push(`h -${triggerRect.width - FORGIVING_PATH_OVERLAP_PX}`);
		d.push(`z`);

		return { svgStyle, pathD: d.join(' ') };
	}

	// ─── Утилиты ────────────────────────────────────────────────────────────

	private static getRect(el: HTMLElement): Rect {
		const r: DOMRect = el.getBoundingClientRect();
		return { top: r.top, left: r.left, width: r.width, height: r.height };
	}
}

export default Popup;